import React, { useState, useEffect, useMemo } from 'react';
import { ACTIVE_GREEN } from '../../../constants/statusColors';
import {
  Box,
  Typography,
  Button,
  List,
  ListItem,
  Chip,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  DescriptionOutlined as DescriptionIcon,
  UploadOutlined as UploadIcon,
} from '@mui/icons-material';
import { csAdminApi } from '../../../services/api';
import { useLookups } from '../../../hooks/useLookups';
import DirectorDocumentFormDialog from '../../../components/directors/DirectorDocumentFormDialog';
import {
  DEFAULT_DIRECTOR_MANDATORY_TYPE_CODES,
  docMatchesMandatoryType,
  findUploadedMandatoryDoc,
  getMissingMandatoryTypeCodes,
  lookupLabel,
  resolveDirectorMandatoryTypeCodes,
  validateStoredDirectorDocument,
} from '../../../utils/mandatoryDocumentTypes';

const DocumentsStep = ({ formData, onChange, errors = {} }) => {
  const { items: lookupDocumentTypes } = useLookups('DIRECTOR_DOCUMENT_TYPE');
  const [mandatoryTypeCodes, setMandatoryTypeCodes] = useState([...DEFAULT_DIRECTOR_MANDATORY_TYPE_CODES]);
  const [loadingSettings, setLoadingSettings] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [activeSlot, setActiveSlot] = useState(null);
  const [otherUploadOpen, setOtherUploadOpen] = useState(false);

  useEffect(() => {
    if (!formData.companyId) {
      setMandatoryTypeCodes([...DEFAULT_DIRECTOR_MANDATORY_TYPE_CODES]);
      onChange({
        ...formData,
        directorMandatoryTypeCodes: [...DEFAULT_DIRECTOR_MANDATORY_TYPE_CODES],
      });
      return;
    }

    let cancelled = false;
    setLoadingSettings(true);
    csAdminApi.getCompanySettings(formData.companyId)
      .then((res) => {
        if (cancelled) return;
        const codes = resolveDirectorMandatoryTypeCodes(
          res.data?.data?.documents || {},
          lookupDocumentTypes
        );
        setMandatoryTypeCodes(codes);
        onChange({
          ...formData,
          directorMandatoryTypeCodes: codes,
        });
      })
      .catch(() => {
        if (!cancelled) {
          setMandatoryTypeCodes([...DEFAULT_DIRECTOR_MANDATORY_TYPE_CODES]);
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingSettings(false);
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.companyId, lookupDocumentTypes]);

  const documentSlots = useMemo(
    () => mandatoryTypeCodes.map((typeCode) => ({
      typeCode,
      typeLabel: lookupLabel(lookupDocumentTypes, typeCode),
      uploaded: findUploadedMandatoryDoc(formData.documents, typeCode, lookupDocumentTypes),
    })),
    [mandatoryTypeCodes, formData.documents, lookupDocumentTypes]
  );

  const otherDocuments = useMemo(
    () => (formData.documents || []).filter(
      (doc) => !mandatoryTypeCodes.some((code) => docMatchesMandatoryType(doc, code, lookupDocumentTypes))
    ),
    [formData.documents, mandatoryTypeCodes, lookupDocumentTypes]
  );

  const missingMandatory = useMemo(
    () => getMissingMandatoryTypeCodes(formData.documents, mandatoryTypeCodes),
    [formData.documents, mandatoryTypeCodes]
  );

  const upsertDocument = (newDoc) => {
    const typeKey = newDoc.typeCode || newDoc.type;
    const filtered = (formData.documents || []).filter(
      (doc) => !docMatchesMandatoryType(doc, typeKey, lookupDocumentTypes)
    );
    onChange({
      ...formData,
      documents: [...filtered, newDoc],
      directorMandatoryTypeCodes: mandatoryTypeCodes,
    });
  };

  const handleOpenSlot = (slot) => {
    setActiveSlot(slot);
    setDialogOpen(true);
  };

  const handleRemoveDocument = (docToRemove) => {
    onChange({
      ...formData,
      documents: (formData.documents || []).filter((doc) => doc !== docToRemove),
      directorMandatoryTypeCodes: mandatoryTypeCodes,
    });
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Director Documents
      </Typography>
      <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
        Required types come from company Settings → Documents → Mandatory Director Document Types.
        Field rules (expiry, document number) follow Lookups → Director Document Types.
      </Typography>

      {loadingSettings ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={28} />
        </Box>
      ) : (
        <>
          {missingMandatory.length > 0 && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              Still required: {missingMandatory.map((c) => lookupLabel(lookupDocumentTypes, c)).join(', ')}
            </Alert>
          )}
          {errors.documents && (
            <Alert severity="error" sx={{ mb: 2 }}>{errors.documents}</Alert>
          )}

          <List sx={{ p: 0, mb: 2 }}>
            {documentSlots.map((slot, index) => (
              <ListItem
                key={slot.typeCode}
                sx={{
                  px: 0,
                  py: 1.5,
                  borderBottom: index < documentSlots.length - 1 ? '1px solid #E0E0E0' : 'none',
                  alignItems: 'flex-start',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, width: '100%' }}>
                  <DescriptionIcon sx={{ mt: 0.5, color: slot.uploaded ? ACTIVE_GREEN : 'text.disabled' }} />
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body1" fontWeight={500}>{slot.typeLabel}</Typography>
                    {slot.uploaded ? (
                      <Typography variant="caption" color="textSecondary" display="block">
                        {slot.uploaded.fileName}
                        {slot.uploaded.expiryDate ? ` · Expires ${slot.uploaded.expiryDate}` : ''}
                      </Typography>
                    ) : (
                      <Typography variant="caption" color="textSecondary">Not uploaded</Typography>
                    )}
                  </Box>
                  <Button
                    size="small"
                    variant={slot.uploaded ? 'outlined' : 'contained'}
                    startIcon={<UploadIcon />}
                    onClick={() => handleOpenSlot(slot)}
                  >
                    {slot.uploaded ? 'Replace' : 'Upload'}
                  </Button>
                </Box>
              </ListItem>
            ))}
          </List>

          <Button
            variant="outlined"
            startIcon={<UploadIcon />}
            onClick={() => setOtherUploadOpen(true)}
            sx={{ mb: 2 }}
          >
            Upload other document
          </Button>

          {otherDocuments.length > 0 && (
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>Other documents</Typography>
              <List sx={{ bgcolor: 'grey.50', borderRadius: 1 }}>
                {otherDocuments.map((doc, index) => (
                  <ListItem
                    key={`${doc.typeCode || doc.type}-${index}`}
                    sx={{ borderBottom: '1px solid', borderColor: 'divider' }}
                    secondaryAction={
                      <Button size="small" color="error" onClick={() => handleRemoveDocument(doc)}>
                        Remove
                      </Button>
                    }
                  >
                    <Box>
                      <Typography variant="body2" fontWeight={500}>{doc.fileName}</Typography>
                      <Chip label={doc.typeLabel || doc.typeCode} size="small" variant="outlined" sx={{ mt: 0.5 }} />
                    </Box>
                  </ListItem>
                ))}
              </List>
            </Box>
          )}
        </>
      )}

      <DirectorDocumentFormDialog
        open={dialogOpen}
        onClose={() => {
          setDialogOpen(false);
          setActiveSlot(null);
        }}
        presetDocumentType={activeSlot ? { code: activeSlot.typeCode, label: activeSlot.typeLabel } : null}
        existingDoc={activeSlot?.uploaded}
        onSave={upsertDocument}
      />

      <DirectorDocumentFormDialog
        open={otherUploadOpen}
        onClose={() => setOtherUploadOpen(false)}
        onSave={upsertDocument}
      />
    </Box>
  );
};

export function validateDirectorDocumentsStep(formData, lookupDocumentTypes) {
  const mandatoryCodes = formData.directorMandatoryTypeCodes || DEFAULT_DIRECTOR_MANDATORY_TYPE_CODES;
  const missing = getMissingMandatoryTypeCodes(formData.documents, mandatoryCodes);
  if (missing.length) {
    return `Please upload required documents: ${missing.map((c) => lookupLabel(lookupDocumentTypes, c)).join(', ')}`;
  }
  for (const doc of formData.documents || []) {
    const fieldError = validateStoredDirectorDocument(doc, lookupDocumentTypes);
    if (fieldError) return fieldError;
  }
  return null;
}

export default DocumentsStep;
